import React from 'react';
import { Button, Stack, SxProps, Typography } from '@mui/material';
import { mdiArrowLeftThick, mdiRefresh } from '@mdi/js';
import { fullSize } from '../../../Constants';
import BgImage from '../../General/BackgroundImage/BgImage';
import AppIcon from '../../General/Icons/AppIcon';
import { useTranslation } from 'react-i18next';
import { headingFontBold, bodyFont, vars } from '../../../Theme';
import i18n from '../../../localization/i18n';

const linkButtonSx: SxProps = {
    display: 'flex',
    flexDirection: 'row',
    borderRadius: '6px',
    padding: '2px',
    textTransform: 'none',
    '&:hover': { backgroundColor: vars.selectedBgGreen },
};

const linkLabelSx: SxProps = {
    fontWeight: 'bold',
    fontSize: '16px',
    color: vars.brandGreen,
};

interface IGeneralErrorViewProps {
    replicaId: string;
}

export default ({ replicaId }: IGeneralErrorViewProps) => {
    const { t } = useTranslation('translations', { keyPrefix: 'errorView' });

    const informationLink = {
        en: 'https://help.thinkproject.com/public/en/Content/CDI/CDI%20Produktinformationen/Systemvoraussetzungen%20CDE%20Infrastructure.htm',
        de: 'https://help.thinkproject.com/public/de/Content/CDI/CDI%20Produktinformationen/Systemvoraussetzungen%20CDE%20Infrastructure.htm',
    };

    return (
        <Stack
            sx={{
                alignItems: 'center',
                justifyContent: 'center',
                ...fullSize,
            }}
        >
            <BgImage imageFile={'general-error-bg.jpg'} />
            <Stack
                direction='column'
                sx={{
                    alignItems: 'center',
                    maxWidth: '560px',
                    p: 2,
                }}
            >
                <Typography
                    variant='h3'
                    sx={{
                        color: 'error',
                    }}
                >
                    {t('oops')}
                </Typography>

                <Typography
                    sx={{
                        fontSize: '16px',
                        fontWeight: 'bold',
                        textAlign: 'center',
                        marginTop: '20px',
                    }}
                >
                    {t('errorDescription')}
                    <Button
                        sx={{
                            borderRadius: '6px',
                            padding: '2px',
                            textTransform: 'none',
                            fontSize: '16px',
                            fontFamily: bodyFont,
                            fontWeight: 'bold',
                            height: '24px',
                            '&:hover': { backgroundColor: vars.selectedBgGreen },
                        }}
                        href={informationLink[i18n.language] ?? informationLink.en}
                    >
                        {t('informationLink')}
                    </Button>
                </Typography>

                <Button
                    onClick={() => window.location.reload()}
                    variant='contained'
                    sx={{ marginTop: '30px', padding: '8px 12px' }}
                >
                    <AppIcon
                        path={mdiRefresh}
                        color={vars.white}
                        withLabel={true}
                        label={t('refreshPage')}
                        labelSx={{
                            ...linkLabelSx,
                            fontFamily: headingFontBold,
                            color: vars.white,
                            fontSize: '16px',
                            textTransform: 'none',
                        }}
                    />
                </Button>

                <Button
                    href={`/project/${replicaId}`}
                    disabled={!replicaId}
                    sx={{ ...linkButtonSx, marginTop: '30px' }}
                >
                    <AppIcon
                        path={mdiArrowLeftThick}
                        color={!replicaId ? vars.inactiveGreen : vars.brandGreen}
                        withLabel={true}
                        label={t('documentViewSelection')}
                        labelSx={
                            !replicaId ? { ...linkLabelSx, color: vars.inactiveGreen } : linkLabelSx
                        }
                    />
                </Button>

                <Button href='/' sx={{ ...linkButtonSx, marginTop: '8px' }}>
                    <AppIcon
                        path={mdiArrowLeftThick}
                        color={vars.brandGreen}
                        withLabel={true}
                        label={t('projectSelection')}
                        labelSx={linkLabelSx}
                    />
                </Button>
            </Stack>
        </Stack>
    );
};
